/**
 * @fileoverview Per-customer shopping cart (Pinia).
 *
 * Persistence: `localStorage` key `cart_${userId}` (JSON array of line items).
 * Requires an authenticated customer (`useAuthStore`); mutating actions no-op or throw if logged out.
 *
 * User switching: when `auth.user.id` changes, the in-memory cart is saved under the *previous* user id,
 * then the new user’s saved cart is loaded. This must not use `saveCart()` during the switch because
 * `saveCart` refuses to write when `auth.user.id !== userId` (guards against cross-user writes).
 *
 * Line items (from `addToCart`): `item_id`, `name`, `price`, optional `discounted`, `final_price`,
 * `discount_price` (unit price after discount), `discount_percentage` (display % from API), `quantity`,
 * optional color fields (`color_id`, `color_label`, `color_primary`, `color_secondary`, `color_source`).
 *
 * Line identity = `item_id` + optional `color_id` (see `./colorVariants.js`).
 */

import { defineStore } from "pinia";
import { useAuthStore } from "../authen/auth";
import { ensureItemSizeSlots } from "../utils/orderSizes";
import {
  findCartLine,
  cartLinesMatch,
  cartLineKeyFromItem,
  normalizeColorId,
} from "./colorVariants";

/**
 * Percent off (1–100) from line or product-shaped object.
 * Storefront convention matches WishList / product_comp: API `discount_price` is % when discounted.
 */
function discountPercentFromLine(item) {
  if (!item?.discounted) return null;
  if (item.discount_percentage != null && item.discount_percentage !== "") {
    const n = parseFloat(item.discount_percentage);
    if (Number.isFinite(n) && n > 0 && n <= 100) return n;
  }
  if (item.discount_price != null && item.discount_price !== "") {
    const n = parseFloat(item.discount_price);
    const list = Number(item.price);
    if (!Number.isFinite(n) || !Number.isFinite(list) || list <= 0) return null;
    if (n > 0 && n <= 100) return n;
    if (n > 0 && n < list) return Math.round((100 * (1 - n / list)) * 100) / 100;
  }
  return null;
}

/** Unit price after discount, or list price (aligned with WishList.getCurrentPrice). */
function unitLinePrice(item) {
  const list = Number(item?.price);
  if (!Number.isFinite(list) || !item) return Number.isFinite(list) ? list : 0;
  if (!item.discounted) return list;
  const pct = discountPercentFromLine(item);
  if (pct != null) {
    return Math.round(list * (1 - pct / 100) * 100) / 100;
  }
  const fp = item.final_price != null ? Number(item.final_price) : NaN;
  if (Number.isFinite(fp) && fp >= 0 && fp <= list) return fp;
  const du = item.discount_price != null ? Number(item.discount_price) : NaN;
  if (Number.isFinite(du) && du >= 0 && du < list) return du;
  return list;
}

function unitListPrice(item) {
  const p = Number(item?.price);
  return Number.isFinite(p) ? p : 0;
}

export const useCartStore = defineStore("cart", {
  state: () => ({
    items: [],
    userId: null,
    storagePrefix: "cart_",
  }),

  getters: {
    cartCount: (state) =>
      state.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),

    cartTotal: (state) =>
      state.items.reduce(
        (sum, item) => sum + unitLinePrice(item) * (Number(item.quantity) || 0),
        0
      ),

    cartSubtotal: (state) =>
      state.items.reduce(
        (sum, item) => sum + unitListPrice(item) * (Number(item.quantity) || 0),
        0
      ),

    totalSavings: (state) =>
      state.items.reduce((sum, item) => {
        if (!item.discounted) return sum;
        const u = unitLinePrice(item);
        const list = unitListPrice(item);
        if (list > u) return sum + (list - u) * (Number(item.quantity) || 0);
        return sum;
      }, 0),

    isEmpty: (state) => state.items.length === 0,
  },

  actions: {
    _customerId(auth) {
      return auth?.user?.id ?? null;
    },

    /** Write `this.items` to localStorage for a specific user (no auth checks). */
    _persistItemsForUserId(uid) {
      if (uid == null) return;
      try {
        localStorage.setItem(
          this.storagePrefix + uid,
          JSON.stringify(this.items)
        );
      } catch (e) {
        console.warn("[cart] Failed to persist cart:", e);
      }
    },

    /** Replace `this.items` from localStorage for `uid`; invalid/missing data becomes []. */
    _loadItemsForUserId(uid) {
      if (uid == null) {
        this.items = [];
        return;
      }
      const key = this.storagePrefix + uid;
      try {
        const raw = localStorage.getItem(key);
        if (raw == null || raw === "") {
          this.items = [];
          return;
        }
        const parsed = JSON.parse(raw);
        this.items = Array.isArray(parsed) ? parsed : [];
        this.items.forEach((item) => ensureItemSizeSlots(item));
      } catch {
        this.items = [];
      }
    },

    /**
     * Ensures cart state matches the logged-in user; clears cart if logged out.
     * @returns {boolean} true if authenticated and aligned
     */
    validateAuth() {
      const auth = useAuthStore();

      if (!auth.isAuthenticated || !auth.user) {
        if (this.items.length > 0 || this.userId != null) {
          this.clearSessionCart();
        }
        return false;
      }

      const currentUserId = this._customerId(auth);

      if (this.userId != null && this.userId !== currentUserId) {
        this._persistItemsForUserId(this.userId);
        this.userId = currentUserId;
        this._loadItemsForUserId(this.userId);
        return true;
      }

      if (this.userId == null) {
        this.userId = currentUserId;
        this._loadItemsForUserId(this.userId);
      }

      return true;
    },

    initCart() {
      const auth = useAuthStore();

      if (!auth.isAuthenticated || !auth.user) {
        this.clearSessionCart();
        return;
      }

      this.userId = this._customerId(auth);
      this._loadItemsForUserId(this.userId);
    },

    handleLoginSession() {
      const auth = useAuthStore();

      if (!auth.isAuthenticated || !auth.user) {
        this.clearSessionCart();
        return;
      }

      const newUserId = this._customerId(auth);

      if (this.userId != null && this.userId !== newUserId) {
        this._persistItemsForUserId(this.userId);
      }

      this.userId = newUserId;
      this._loadItemsForUserId(this.userId);
      this.saveCart();
    },

    saveCart() {
      const auth = useAuthStore();

      if (!auth.isAuthenticated || this.userId == null) {
        if (this.items.length > 0) {
          this.clearSessionCart();
        }
        return;
      }

      if (auth.user && auth.user.id !== this.userId) {
        return;
      }

      this._persistItemsForUserId(this.userId);
    },

    clearSessionCart() {
      this.items = [];
      this.userId = null;
    },

    logout() {
      this.clearSessionCart();
    },

    /**
     * Reconcile with auth (e.g. after token refresh). Same alignment rules as validateAuth.
     */
    syncWithAuth() {
      const auth = useAuthStore();

      if (!auth.isAuthenticated || !auth.user) {
        if (this.items.length > 0 || this.userId != null) {
          this.clearSessionCart();
        }
        return false;
      }

      const currentUserId = this._customerId(auth);

      if (this.userId == null) {
        this.userId = currentUserId;
        this._loadItemsForUserId(this.userId);
        return true;
      }

      if (this.userId !== currentUserId) {
        this._persistItemsForUserId(this.userId);
        this.userId = currentUserId;
        this._loadItemsForUserId(this.userId);
        return true;
      }

      return true;
    },

    addToCart(product) {
      if (!this.validateAuth()) {
        console.warn(
          "[cart] Cannot add: user not authenticated or session expired"
        );
        throw new Error("Please log in to add items to your cart");
      }

      const colorId = normalizeColorId(product.color_id);
      const existing = findCartLine(this.items, {
        item_id: product.item_id,
        color_id: colorId,
      });

      if (existing) {
        existing.quantity = (Number(existing.quantity) || 0) + 1;
        ensureItemSizeSlots(existing);
      } else {
        const list = Number(product.price);
        const pctRaw =
          product.discount_percentage != null && product.discount_percentage !== ""
            ? parseFloat(product.discount_percentage)
            : product.discounted && product.discount_price != null
              ? parseFloat(product.discount_price)
              : NaN;
        let finalPrice = Number.isFinite(list) ? list : 0;
        let unitAfterDiscount = finalPrice;
        let displayPct = null;

        if (
          product.discounted &&
          Number.isFinite(pctRaw) &&
          pctRaw > 0 &&
          pctRaw <= 100 &&
          Number.isFinite(list)
        ) {
          displayPct = Math.round(pctRaw * 100) / 100;
          unitAfterDiscount = Math.round(list * (1 - pctRaw / 100) * 100) / 100;
          finalPrice = unitAfterDiscount;
        }

        this.items.push({
          item_id: product.item_id,
          name: product.name,
          price: product.price,
          final_price: finalPrice,
          discount_price: unitAfterDiscount,
          discount_percentage: product.discounted ? displayPct : null,
          discounted: Boolean(product.discounted),
          thumbnail: product.thumbnail || product.image_url || null,
          quantity: 1,
          currency: product.currency || "KES",
          sub_category: product.sub_category || product.subcategory || null,
          sizes: [],
          // Color variant (null for thumbnail / non-variant adds)
          color_id: colorId,
          color_label: product.color_label || null,
          color_primary: product.color_primary
            ? String(product.color_primary).replace(/^#/, "").toLowerCase()
            : null,
          color_secondary: product.color_secondary
            ? String(product.color_secondary).replace(/^#/, "").toLowerCase()
            : null,
          color_source: product.color_source || null,
          line_key: cartLineKeyFromItem({
            item_id: product.item_id,
            color_id: colorId,
          }),
        });
        ensureItemSizeSlots(this.items[this.items.length - 1]);
      }

      this.saveCart();
    },

    increaseQty(product) {
      if (!this.validateAuth()) {
        console.warn("[cart] Cannot increase qty: not authenticated");
        return;
      }

      const item = findCartLine(this.items, {
        item_id: product.item_id,
        color_id: product.color_id,
      });
      if (item) {
        item.quantity = (Number(item.quantity) || 0) + 1;
        ensureItemSizeSlots(item);
        this.saveCart();
      }
    },

    decreaseQty(product) {
      if (!this.validateAuth()) {
        console.warn("[cart] Cannot decrease qty: not authenticated");
        return;
      }

      const item = findCartLine(this.items, {
        item_id: product.item_id,
        color_id: product.color_id,
      });
      if (!item) return;

      const q = Number(item.quantity) || 0;
      if (q > 1) {
        item.quantity = q - 1;
        ensureItemSizeSlots(item);
      } else {
        this.items = this.items.filter((i) => !cartLinesMatch(i, item));
      }

      this.saveCart();
    },

    /**
     * Remove a cart line.
     * @param {number|string|object} productIdOrLine - item_id, or line `{ item_id, color_id }`
     * @param {string|null} [colorId] - when first arg is item_id
     */
    removeFromCart(productIdOrLine, colorId = null) {
      if (!this.validateAuth()) {
        console.warn("[cart] Cannot remove: not authenticated");
        return;
      }

      const ref =
        productIdOrLine != null &&
        typeof productIdOrLine === "object" &&
        !Array.isArray(productIdOrLine)
          ? {
              item_id: productIdOrLine.item_id,
              color_id: productIdOrLine.color_id,
            }
          : { item_id: productIdOrLine, color_id: colorId };

      this.items = this.items.filter((i) => !cartLinesMatch(i, ref));
      this.saveCart();
    },

    clearCart() {
      if (!this.validateAuth()) {
        console.warn("[cart] Cannot clear: not authenticated");
        return;
      }

      this.items = [];
      this.saveCart();
    },

    /**
     * Direct quantity set (e.g. ProductView). Removes line if quantity &lt;= 0.
     * @param {number|string|object} productIdOrLine
     * @param {number} quantity
     * @param {string|null} [colorId]
     */
    updateQuantity(productIdOrLine, quantity, colorId = null) {
      if (!this.validateAuth()) {
        console.warn("[cart] Cannot update quantity: not authenticated");
        return;
      }

      const ref =
        productIdOrLine != null &&
        typeof productIdOrLine === "object" &&
        !Array.isArray(productIdOrLine)
          ? {
              item_id: productIdOrLine.item_id,
              color_id: productIdOrLine.color_id,
            }
          : { item_id: productIdOrLine, color_id: colorId };

      const item = findCartLine(this.items, ref);
      if (!item) return;

      const q = Number(quantity);
      if (q > 0) {
        item.quantity = q;
        ensureItemSizeSlots(item);
        this.saveCart();
      } else {
        this.removeFromCart(ref);
      }
    },

    /**
     * @param {number|string|object} productIdOrLine
     * @param {number} unitIndex
     * @param {string} sizeValue
     * @param {string|null} [colorId]
     */
    setItemSize(productIdOrLine, unitIndex, sizeValue, colorId = null) {
      if (!this.validateAuth()) return;

      const ref =
        productIdOrLine != null &&
        typeof productIdOrLine === "object" &&
        !Array.isArray(productIdOrLine)
          ? {
              item_id: productIdOrLine.item_id,
              color_id: productIdOrLine.color_id,
            }
          : { item_id: productIdOrLine, color_id: colorId };

      const item = findCartLine(this.items, ref);
      if (!item) return;
      ensureItemSizeSlots(item);
      if (unitIndex < 0 || unitIndex >= item.sizes.length) return;
      item.sizes[unitIndex] = sizeValue;
      this.saveCart();
    },

    syncAllItemSizeSlots() {
      this.items.forEach((item) => ensureItemSizeSlots(item));
    },

    getItemPrice(item) {
      return unitLinePrice(item);
    },

    getItemTotal(item) {
      return unitLinePrice(item) * (Number(item.quantity) || 0);
    },

    getItemSavings(item) {
      if (!item.discounted) return 0;
      const list = unitListPrice(item);
      const line = unitLinePrice(item);
      if (list > line) return (list - line) * (Number(item.quantity) || 0);
      return 0;
    },
  },
});
